import { CfbBudget, CFB_BROWSER_LIMITS } from './cfb-budget';
/** Adapter contract: acemind.cfb.adapter.v1. Fixture and authenticated DEV transports stay separate. */
export interface CfbPlayer {
  rowIndex: number; canonicalPlayerId: string; sitePlayerId: string;
  name: string; positions: string[]; salary: number; projectedPoints: number;
}
export interface CfbInput {
  inputId: string; inputHash: string; slotColumns: string[]; players: CfbPlayer[];
}
export interface CfbStatus {
  sessionId: string; state: 'queued' | 'running' | 'pausing' | 'paused' | 'failed' | 'exhausted';
  generation: number; controlRevision: number; candidateCount: number;
  resultVersion: number; reason: string | null;
}
export interface CfbCandidate {
  candidateId: string; sitePlayerIds: string[]; canonicalPlayerIds: string[];
  projectedPoints: number; salary: number;
}
export interface CfbPage {
  sessionId: string; resultVersion: number; rows: CfbCandidate[];
  slotColumns: string[]; totalCandidates: number; nextCursor: string | null;
}
export interface CfbSelection {
  selectionId: string; resultVersion: number; entryCount: number;
  exportProfile: string; exportSha256: string;
  scoreVersion?: number; scoreHash?: string; metricsExportSha256?: string;
}
export interface CfbScoreRow {
  candidateId: string; count: number; sitePlayerIds: string[]; canonicalPlayerIds?: string[];
  mean_payout: number; roi: number | null; p_win: number; p_cash: number;
  p_top1pct: number; mean_rank: number; dupes: number;
}
export interface CfbScores {
  schemaVersion: string; syntheticOnly: boolean; sessionId: string; selectionId: string;
  resultVersion: number; scoreVersion: number; slotColumns: string[]; rows: CfbScoreRow[];
  inputRef: string; selectionHash: string; outcomeHash: string; fieldHash: string;
  simulationHash: string; scorerVersion: string; modelVersion: string; scoringVersion: string;
  entriesCheck: Record<string, number>;
}
export interface CfbTransport {
  request<T>(path: string, body?: unknown): Promise<T>;
  csv(path: string): Promise<Blob>;
  readonly limitReason?: string;
  stop?(reason: string): void;
  dispose?(): void;
}
export class CfbHttpError extends Error {
  constructor(public status: number, message: string, public code?: string, public reasonCode?: string) { super(message); }
}
export interface CfbUser { uid: string; getIdToken(forceRefresh?: boolean): Promise<string>; }
export interface CfbIdentity { uid: string; currentUser(): CfbUser | null; }
export class CfbClient implements CfbTransport {
  constructor(private base = '/v1/cfb', private fetcher: typeof fetch = globalThis.fetch.bind(globalThis),
    private identity?: CfbIdentity, private fixture = true, private budget?: CfbBudget) {
    if (!fixture && !identity) throw new Error('CFB hosted requests require an authenticated identity.');
  }
  private disposed = false;
  private controllers = new Set<AbortController>();
  get limitReason() { return this.budget?.reason || (this.disposed ? 'Browser view closed.' : ''); }
  stop(reason: string) {
    try { this.budget?.stop(reason); }
    finally { this.dispose(); } // Storage failure must never leave requests enabled.
  }
  dispose() { this.disposed = true; this.controllers.forEach(controller => controller.abort()); }
  private user() {
    const user = this.identity?.currentUser();
    if (this.identity && (!user || user.uid !== this.identity.uid)) {
      throw new CfbHttpError(401, 'Your account changed. Reopen CFB with the signed-in account.', 'account_changed');
    }
    return user;
  }
  private async response(path: string, body?: unknown): Promise<Response> {
    // Serialize once so an auth refresh cannot change an idempotent mutation's payload.
    const serialized = body === undefined ? undefined : JSON.stringify(body);
    for (let attempt = 0; attempt < 2; attempt++) {
      if (this.limitReason) throw new Error(this.limitReason);
      const controller = new AbortController(); this.controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), Math.min(CFB_BROWSER_LIMITS.requestTimeoutMs, this.budget?.remainingMs ?? CFB_BROWSER_LIMITS.requestTimeoutMs));
      // Bound token acquisition as well as fetch/body reads, even if a dependency ignores AbortSignal.
      const abort = new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => reject(new Error('CFB request stopped or timed out. Retry an uncertain action only with its saved request.')), { once: true }));
      const bounded = <T>(work: Promise<T>) => Promise.race([work, abort]);
      try {
        const user = this.user();
        let headers: Record<string, string> = {};
        if (this.fixture) headers['X-Fixture-User'] = 'peter';
        else {
          let token: string;
          try { token = await bounded(user!.getIdToken(attempt === 1)); }
          catch { if (controller.signal.aborted) throw new Error('CFB token request stopped or timed out.'); throw new CfbHttpError(401, 'Sign in again to access CFB.', 'token_unavailable'); }
          this.user();
          if (!token) throw new CfbHttpError(401, 'Sign in again to access CFB.', 'token_unavailable');
          headers.Authorization = `Bearer ${token}`;
        }
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        if (controller.signal.aborted) throw new Error('CFB request timed out.');
        if (this.limitReason) throw new Error(this.limitReason);
        this.budget?.claim(); // Includes auth retries and CSV reads, not only status polls.
        const response = await bounded(this.fetcher(this.base + path, {
          method: body === undefined ? 'GET' : 'POST', headers, body: serialized,
          cache: 'no-store', signal: controller.signal,
        }));
        this.user();
        if (response.status === 401 && !this.fixture && attempt === 0) {
          await bounded(response.body?.cancel() || Promise.resolve());
          continue; // One refresh only; no fallback to fixture authentication.
        }
        if (!response.ok) {
          let message = `CFB request failed (${response.status})`; let code: string | undefined; let reasonCode: string | undefined;
          try {
            const data = await bounded(response.json());
            message = data.message || data.error || message;
            code = typeof data.error === 'string' ? data.error : undefined;
            reasonCode = typeof data.reasonCode === 'string' && data.reasonCode.length > 0 ? data.reasonCode : undefined;
          } catch { /* Gateway/auth errors may be HTML. */ }
          if ([401, 403].includes(response.status)) message = 'Sign in with an approved DEV account to access CFB. Your saved selections are retained.';
          if (response.status === 404) message = 'This CFB resource is unavailable to the signed-in account. Check the account and session link.';
          throw new CfbHttpError(response.status, String(message), code, reasonCode);
        }
        const blob = await bounded(response.blob());
        this.user(); // Discard late JSON/CSV responses after sign-out/account switch.
        return new Response(blob, { status: response.status, headers: response.headers });
      } finally { clearTimeout(timeout); this.controllers.delete(controller); }
    }
    throw new CfbHttpError(401, 'Sign in with an approved DEV account to access CFB.');
  }
  async request<T>(path: string, body?: unknown): Promise<T> { return (await this.response(path, body)).json(); }
  async csv(path: string): Promise<Blob> { return (await this.response(path)).blob(); }
}
